import React, { useRef } from 'react';
import { Card, Typography, Space, theme, Button } from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  ResponsiveContainer, Tooltip, ReferenceLine, LineChart, Line, Legend
} from 'recharts';
import { DownloadOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';

const { Title } = Typography;

const NonFuzzyInconsistency = ({ 
  method,
  variant,
  criteria,
  crisp_weights,
  inconsistency_ratios,
  matrix,
  bestIdx,
  worstIdx
}) => {
  const { token } = theme.useToken();
  const [vizMode, setVizMode] = React.useState('lollipop');
  const [hoveredPair, setHoveredPair] = React.useState(null);
  const chartRef = useRef(null);
  const SUB_I = String.fromCharCode(0x1d62);
  const SUB_J = String.fromCharCode(0x2c7c);
  const ratioLabel = `Inconsistency Ratio (r${SUB_I}${SUB_J})`;
  const declaredLabel = `a${SUB_I}${SUB_J}`;
  const impliedLabel = `w${SUB_I} / w${SUB_J}`;
  const figureExplanations = {
    lollipop: 'Lollipop: each dot is one criterion pair. The dashed baseline at 1 means perfectly matched judgment (implied ratio equals declared ratio). The farther from 1, the larger that pair-specific inconsistency.',
    bar: `Bar: compares declared judgment ${declaredLabel} (input) against implied ratio ${impliedLabel} (from solved weights) for each pair. The gap between the two bars shows how much that pair disagrees with the final weights.`,
    heatmap: 'Heatmap: each cell is the inconsistency ratio for a pair. Values near 1 are more consistent; warmer/cooler colors indicate larger deviation direction and magnitude from 1.',
  };

  const round3 = (x) => (
    typeof x === 'number' && !isNaN(x) 
      ? Math.round(x * 1000) / 1000 
      : 0
  );

  const toFiniteNumber = (value) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const safeDivide = (num, den) => {
    const nVal = toFiniteNumber(num);
    const dVal = toFiniteNumber(den);
    if (nVal === null || dVal === null || dVal === 0) return null;
    return nVal / dVal;
  };

  const isAhpNonFuzzy = method === 'ahp' && variant === 'origin';
  const isBwmNonFuzzy = method === 'bwm' && ['linear', 'nonlinear'].includes(variant);
  const n = criteria.length;

  // Prepare inconsistency data
  let inconsistencyData = [];

  if (isAhpNonFuzzy && Array.isArray(inconsistency_ratios) && inconsistency_ratios.length > 0) {
    for (let i = 0; i < n; i++) {
      for (let j = i; j < n; j++) {
        const w_i = crisp_weights[i];
        const w_j = crisp_weights[j];
        const implied = (typeof w_i === 'number' && typeof w_j === 'number' && w_j !== 0)
          ? w_i / w_j : null;

        inconsistencyData.push({
          i, j,
          pair: `${criteria[i]} vs ${criteria[j]}`,
          value: toFiniteNumber(inconsistency_ratios[i]?.[j]),
          declared: matrix[i][j],
          implied,
        });
      }
    }
  }

  if (
    isBwmNonFuzzy &&
    Number.isInteger(bestIdx) &&
    Number.isInteger(worstIdx) &&
    bestIdx >= 0 &&
    worstIdx >= 0 &&
    bestIdx < n &&
    worstIdx < n
  ) {
    const bwo = Array.isArray(inconsistency_ratios?.bwo) ? inconsistency_ratios.bwo : [];
    const wwo = Array.isArray(inconsistency_ratios?.wwo) ? inconsistency_ratios.wwo : [];
    const w_best = crisp_weights[bestIdx];
    const w_worst = crisp_weights[worstIdx];

    for (let j = 0; j < n; j++) {
      const w_j = crisp_weights[j];
      const implied = safeDivide(w_best, w_j);
      const declared = matrix?.[bestIdx]?.[j];
      const fromApi = toFiniteNumber(bwo[j]);
      const fallback = safeDivide(implied, declared);

      inconsistencyData.push({
        i: bestIdx, j,
        pair: `${criteria[bestIdx]} vs ${criteria[j]}`,
        value: fromApi ?? fallback,
        declared,
        implied,
      });
    }

    for (let i = 0; i < n; i++) {
      const w_i = crisp_weights[i];
      const implied = safeDivide(w_i, w_worst);
      const declared = matrix?.[i]?.[worstIdx];
      const fromApi = toFiniteNumber(wwo[i]);
      const fallback = safeDivide(implied, declared);

      inconsistencyData.push({
        i, j: worstIdx,
        pair: `${criteria[i]} vs ${criteria[worstIdx]}`,
        value: fromApi ?? fallback,
        declared,
        implied,
      });
    }
  }

  // Prepare chart data
  const ratioData = inconsistencyData
    .map(d => ({
      pair: d.pair,
      value: toFiniteNumber(d.value),
    }))
    .filter(d => d.value !== null);

  const barData = inconsistencyData.map(d => ({
    pair: d.pair,
    declared: toFiniteNumber(d.declared),
    implied: toFiniteNumber(d.implied),
  }));

  const slopeCount = inconsistencyData.length;
  const slopeData = inconsistencyData.map((d, idx) => ({
    pair: d.pair,
    declared: d.declared,
    implied: d.implied,
    color: `hsl(${(idx * 360) / slopeCount}, 70%, 55%)`,
  }));

  // Heatmap data
  let heatmapData = [];
  let heatMax = 0;

  if (isAhpNonFuzzy && Array.isArray(inconsistency_ratios)) {
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const ratio = toFiniteNumber(inconsistency_ratios[i]?.[j]);
        const diff = ratio !== null ? Math.abs(ratio - 1) : 0;
        heatmapData.push({
          row: criteria[i],
          col: criteria[j],
          value: ratio,
          diff,
        });
      }
    }
    const diffs = heatmapData.map(d => d.diff);
    heatMax = diffs.length > 0 ? Math.max(...diffs) : 0;
  }

  if (isBwmNonFuzzy && inconsistencyData.length > 0) {
    for (let j = 0; j < n; j++) {
      const ratio = toFiniteNumber(inconsistencyData.find((d) => d.i === bestIdx && d.j === j)?.value);
      const diff = ratio !== null ? Math.abs(ratio - 1) : 0;
      heatmapData.push({
        row: criteria[bestIdx],
        col: criteria[j],
        value: ratio,
        diff,
      });
    }

    for (let i = 0; i < n; i++) {
      const ratio = toFiniteNumber(inconsistencyData.find((d) => d.i === i && d.j === worstIdx)?.value);
      const diff = ratio !== null ? Math.abs(ratio - 1) : 0;
      heatmapData.push({
        row: criteria[i],
        col: criteria[worstIdx],
        value: ratio,
        diff,
      });
    }
    const diffs = heatmapData.map(d => d.diff);
    heatMax = diffs.length > 0 ? Math.max(...diffs) : 0;
  }

  // Calibrated from random reciprocal-matrix simulation:
  // for CR <= 0.1 cases, |ratio-1| is usually <= ~0.4 (95th percentile).
  // Keep a neutral band near 1 and avoid overreacting when all values are tightly clustered.
  const HEATMAP_NEUTRAL_EPS = 0.01;
  const HEATMAP_BASELINE_SATURATION_DELTA = 0.4;
  const HEATMAP_GAMMA = 1.6;
  const heatSaturationDelta = Math.max(
    HEATMAP_BASELINE_SATURATION_DELTA,
    (heatMax || 0) * 0.35
  );

  // Y-axis scale for lollipop
  let minTick = 0, maxTick = 0, yTicks = [];
  if (ratioData.length > 0) {
    const maxRatioValue = Math.max(...ratioData.map(d => d.value), 1.5);
    const minRatioValue = Math.min(...ratioData.map(d => d.value));
    const tickStep = 0.5;
    maxTick = Math.ceil(maxRatioValue / tickStep) * tickStep;
    minTick = Math.max(0, Math.floor(minRatioValue * 0.8 / tickStep) * tickStep);
    if (maxTick <= minTick) {
      maxTick = minTick + tickStep;
    }

    yTicks = [];
    for (let t = minTick; t <= maxTick + 1e-9; t += tickStep) {
      yTicks.push(Number(t.toFixed(2)));
    }
  }

  // Slope chart scale
  const slopeRawValues = slopeData.flatMap(d => [d.declared, d.implied])
    .filter(v => typeof v === 'number' && v > 0);
  
  let slopeDomain = ['auto', 'auto'];
  let slopeTicks = [];
  if (slopeRawValues.length > 0) {
    const maxVal = Math.max(...slopeRawValues);
    const minExp = 0;
    const maxExp = Math.ceil(Math.log10(maxVal));
    slopeDomain = [Math.pow(10, minExp), Math.pow(10, maxExp)];
    
    for (let e = minExp; e <= maxExp; e += 1) {
      slopeTicks.push(Math.pow(10, e));
    }
  }

  // Lollipop shape
  const LollipopShape = (props) => {
    const { x, width, payload, background } = props;
    if (!payload || !background) return null;

    const value = payload.value;
    const centerX = x + width / 2;
    const yBottom = background.y + background.height;
    const totalHeight = background.height;
    const range = (maxTick - minTick) || 1e-9;
    const yScale = (v) => yBottom - ((v - minTick) / range) * totalHeight;

    const baselineY = yScale(1);
    const valueY = yScale(value);

    return (
      <g>
        <line x1={centerX} y1={baselineY} x2={centerX} y2={valueY}
          stroke={token.colorPrimary} strokeWidth={2} />
        <circle cx={centerX} cy={valueY} r={5}
          fill={token.colorPrimary} stroke="#fff" strokeWidth={1} />
      </g>
    );
  };

  // Slope tooltip
  const SlopeTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const filtered = hoveredPair
      ? payload.filter(p => p.name === hoveredPair)
      : payload;
    if (!filtered.length) return null;
    const p = filtered[0];

    return (
      <div style={{
        background: 'white',
        border: '1px solid #ccc',
        padding: '6px 8px',
        fontSize: 11,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{p.name}</div>
        <div>{label}: {typeof p.value === 'number' ? p.value.toFixed(3) : p.value}</div>
      </div>
    );
  };

  // Diverging heatmap color:
  // value = 1 is best (green), >1 is warm, <1 is cool.
  // Color uses rounded (display-level) precision so same displayed value => same color.
  const getHeatmapColor = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '#f0f0f0';

    const rounded = Number(value.toFixed(3));
    const delta = rounded - 1;

    if (Math.abs(delta) <= HEATMAP_NEUTRAL_EPS) {
      return 'rgb(141, 214, 123)';
    }

    const adjustedDelta = Math.max(0, Math.abs(delta) - HEATMAP_NEUTRAL_EPS);
    const adjustedRange = Math.max(1e-9, heatSaturationDelta - HEATMAP_NEUTRAL_EPS);
    const linearStrength = Math.max(
      0,
      Math.min(1, adjustedDelta / adjustedRange)
    );
    const strength = Math.pow(linearStrength, HEATMAP_GAMMA);

    const best = { r: 141, g: 214, b: 123 };
    const warm = { r: 235, g: 111, b: 80 };
    const cool = { r: 86, g: 142, b: 230 };
    const target = delta >= 0 ? warm : cool;

    const r = Math.round(best.r + (target.r - best.r) * strength);
    const g = Math.round(best.g + (target.g - best.g) * strength);
    const b = Math.round(best.b + (target.b - best.b) * strength);
    return `rgb(${r}, ${g}, ${b})`;
  };
  const vizchartWidth = Math.max(ratioData.length * 80, 600);

  const handleExport = async () => {
    // heatmap
  if (vizMode === 'heatmap') {
    if (!chartRef.current) {
      console.warn('Heatmap container not found');
      return;
    }

    try {
      const scale = 3; // scale 2~3: 300~600ppi
      const canvas = await html2canvas(chartRef.current, {
        scale,
        useCORS: true,
        backgroundColor: true,
      });

      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'Individual_Inconsistency_heatmap.png';
      a.click();
    } catch (e) {
      console.error('Failed to export heatmap PNG:', e);
    }

    return;
  }

    // lollipop, bar graph
    const svg = chartRef.current?.querySelector("svg");
    if (!svg) {
      console.warn("SVG not found");
      return;
    }

    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svg);

    if (!source.startsWith("<?xml")) {
      source = `<?xml version="1.0" standalone="no"?>\n` + source;
    }

    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `Individual_Inconsistency_${vizMode}.svg`;
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Card style={{ marginTop: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,              
            // marginBottom: 16,
          }}
        >
        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
          Individual Inconsistency
        </Title>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          {vizMode === 'heatmap'
            ? 'Export Individual Inconsistency (.png)'
            : 'Export Individual Inconsistency (.svg)'}
        </Button>
        </div>
        <select
          value={vizMode}
          onChange={(e) => setVizMode(e.target.value)}
          style={{ padding: '2px 6px', borderRadius: 4 }}
        >
          <option value="lollipop">Lollipop</option>
          <option value="bar">Bar</option>
          {/* <option value="slope">Slope</option> */}
          <option value="heatmap">Heatmap</option>
        </select>
      </Space>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>
        {figureExplanations[vizMode]}
      </Typography.Text>

      <div style={{ width: '100%', overflowX: 'auto' }} ref={chartRef}>
        {/* lollipop */}
        {vizMode === 'lollipop' && (
          <div style={{ minWidth: vizchartWidth }}>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={ratioData}
                margin={{ top: 20, right: 40, left: 40, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="category" 
                  dataKey="pair" 
                  tick={{ angle: -30, textAnchor: "end", fontSize: 10 }}
                  interval={0}
                />
                <YAxis type="number" domain={[minTick, maxTick]} ticks={yTicks} />
                <ReferenceLine y={1} strokeDasharray="4 4" stroke="#000" />
                <Tooltip
                  formatter={(v) => [round3(v), ratioLabel]}
                  labelStyle={{ color: "#000" }}
                  itemStyle={{ color: "#000" }}
                />

                {/* Stick and Dot combined */}
                <Bar dataKey="value" shape={<LollipopShape />} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {vizMode === 'bar' && (
          <div style={{ minWidth: vizchartWidth }}>
            {/* BAR CHART (a_ij vs w_i/w_j) */}
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={barData}
                margin={{ top: 20, right: 40, left: 40, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="pair"
                  interval={0}
                  tick={{ angle: -30, textAnchor: "end", fontSize: 10 }}
                />
                <YAxis type="number" domain={[0, 'auto', 0.5]} />
                <Tooltip 
                  formatter={(v) => [round3(v), ratioLabel]}
                  labelStyle={{ color: '#000' }} 
                  itemStyle={{ color: '#000' }} 
                />
                <Legend 
                  layout="horizontal"
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ paddingBottom: 8 }}
                />
                {/* a_ij (yellow) */}
                <Bar dataKey="declared" name={declaredLabel} fill="#fbc02d" />

                {/* w岬?/ w獗?(blue) */}
                <Bar dataKey="implied" name={impliedLabel} fill={token.colorPrimary} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* deactivated slope chart */}
        {/* {vizMode === 'slope' && (
          <ResponsiveContainer width="100%" height={450}>
            <LineChart margin={{ top: 20, right: 60, left: 60, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="category"
                dataKey="category"
                allowDuplicatedCategory={false}
                domain={['Declared', 'Implied']}
                ticks={['Declared', 'Implied']}
              />
              <YAxis 
                scale="log"
                domain={slopeDomain}
                ticks={slopeTicks}
                tickFormatter={(val) => {
                  const exp = Math.round(Math.log10(val));
                  return `10^${exp}`;
                }}
                label={{ value: 'Ratio (10^n)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<SlopeTooltip />} />
              <Legend 
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ paddingLeft: 10 }}
              />
              {slopeData.map((item, idx) => {
                const data = [
                  { category: 'Declared', value: item.declared },
                  { category: 'Implied', value: item.implied },
                ];
                return (
                  <Line
                    key={idx}
                    dataKey="value"
                    data={data}
                    name={item.pair}
                    stroke={item.color}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    onMouseEnter={() => setHoveredPair(item.pair)}
                    onMouseLeave={() => setHoveredPair(null)}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        )} */}

        {vizMode === 'heatmap' && (
          <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: '0px', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `120px repeat(${criteria.length}, 1fr)`,
                gap: '0px',
                width: '100%'
              }}>
                <div></div>
                {criteria.map((col, i) => (
                  <div key={i} style={{ 
                    fontSize: '12px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    padding: '8px',
                    wordBreak: 'break-word'
                  }}>
                    {col}
                  </div>
                ))}
                
                {criteria.map((row, i) => (
                  <React.Fragment key={i}>
                    <div style={{ 
                      fontSize: '12px', 
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '12px',
                      wordBreak: 'break-word'
                    }}>
                      {row}
                    </div>
                    {criteria.map((col, j) => {
                      const cell = heatmapData.find(d => d.row === row && d.col === col);
                      const value = cell?.value;
                      const roundedValue = typeof value === 'number' && !Number.isNaN(value)
                        ? Number(value.toFixed(3))
                        : null;
                      
                      const hasValue = cell && typeof value === 'number' && !Number.isNaN(value);
                      const bgColor = hasValue ? getHeatmapColor(roundedValue) : '#f0f0f0';
                      const deltaAbs = hasValue ? Math.abs((roundedValue ?? 1) - 1) : 0;
                      const adjustedDelta = Math.max(0, deltaAbs - HEATMAP_NEUTRAL_EPS);
                      const adjustedRange = Math.max(1e-9, heatSaturationDelta - HEATMAP_NEUTRAL_EPS);
                      const strength = hasValue
                        ? Math.pow(Math.min(1, adjustedDelta / adjustedRange), HEATMAP_GAMMA)
                        : 0;
                      const textColor =
                        hasValue && strength > 0.65
                          ? '#fff'
                          : hasValue
                            ? '#000'
                            : '#aaa';

                      return (
                        <div
                          key={j}
                          style={{
                            backgroundColor: bgColor,
                            padding: '12px',
                            textAlign: 'center',
                            fontSize: '11px',
                            border: '1px solid #ddd',
                            minHeight: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '500',
                            color: textColor,
                          }}
                        >
                          {hasValue ? roundedValue.toFixed(3) : ''}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '5px',
              fontSize: '12px',
              minWidth: '5px'
            }}>
              <span style={{ fontWeight: '500' }}>Above 1</span>
              <div style={{ 
                width: '24px', 
                height: '72px', 
                background: 'linear-gradient(to top, rgb(141, 214, 123), rgb(235, 111, 80))',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }} />

              <span style={{ fontWeight: '500' }}>1 (Best)</span>
              <div style={{ 
                width: '24px',
                height: '24px',
                background: 'rgb(141, 214, 123)',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }} />
              <span style={{ fontWeight: '500' }}>Below 1</span>
              <div style={{ 
                width: '24px', 
                height: '72px', 
                background: 'linear-gradient(to bottom, rgb(141, 214, 123), rgb(86, 142, 230))',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }} />
              <div style={{ 
                marginTop: '4px', 
                textAlign: 'center',
                fontSize: '11px',
                color: '#666'
              }}>
                Ratio direction
                <br />
                +/-{heatSaturationDelta.toFixed(2)} ~= full intensity
              </div>
            </div>
          </div>
        )}
        {/* <Button onClick={exportSvg}>Export SVG</Button> */}
      </div>
    </Card>
  );
};

export default NonFuzzyInconsistency;
